import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  inject,
  InjectionToken,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { SearchPageOptionsService } from '../../partial/eo-search-panel/search-page-options.service';
import { PaginatedSearchResult } from '../../partial/eo-search-panel/paginated-search-result.model';
import { ApiCallHandler } from '../../common/api-call-handler';
import { EoFormComponent, EoFormPanelConfig } from '../../form';
import { STATUS } from '../../common';
import { TableConfig } from '../../table/model';

export interface IDataService<T> {
  searchCh: ApiCallHandler<PaginatedSearchResult<T> | null>;
  init(): void;
}

interface messageParams {
  message: string;
  header?: string;
  accept?: () => void;
}

export const DATA_SERVICE_TOKEN = new InjectionToken<IDataService<any>>(
  'DataService'
);

@Component({
  selector: 'eo-search-page',
  templateUrl: './eo-search-page.component.html',
  styleUrl: './eo-search-page.component.scss',
})
export class EoSearchPageComponent<T> {
  public confirmationService = inject(ConfirmationService);
  public searchOptions = inject(SearchPageOptionsService);
  private changeDetector = inject(ChangeDetectorRef);
  @ViewChild('eoForm') eoForm?: EoFormComponent;

  viewOptions$ = this.searchOptions?.getViewOptions();
  defaultSearchParam: any;
  searchParam: any;

  submitBtnLabel = 'Αναζήτηση';

  @Input() formConfig: EoFormPanelConfig[] = [];
  @Input() collapsed = true;
  @Input() tableConfig?: TableConfig<T>;
  @Input() loading = false;
  @Input() viewModeVisible = true;
  @Input() actionMenuVisible = true;
  @Input() customFormControls: any;
  @Input() menuActions: MenuItem[] = [];
  @Input() searchServ?: IDataService<T>;
  @Input() totalCount?: number;
  @Input() page?: number;
  @Input() pagesize?: number;
  @Input() paginator = true;
  @Input() initData = true;
  @Input() instructionsVisible = true;
  @Input() instructionsMenuItem?: MenuItem;
  @Input() showClearButton = true;

  @Output() search = new EventEmitter<any>();
  @Output() clearEvent = new EventEmitter<void>();
  @Output() formChange = new EventEmitter<any>();

  constructor(
    @Inject(DATA_SERVICE_TOKEN) protected searchService: IDataService<T>
  ) {
    if (this.searchServ) searchService = this.searchServ;
  }

  ngAfterContentInit(): void {
    this.changeDetector.detectChanges();
  }

  ngOnInit(): void {
    this.searchOptions
      .captureEvent('window:resize')
      .subscribe(() =>
        this.searchOptions.onIsMobileChange(window.innerWidth < 768)
      );
    this.eoForm?.form?.reset();
    if (this.initData) this.onSubmit(this.defaultSearchParam);
  }

  onViewModeChange($event: 'GRID' | 'TABLE') {
    this.searchOptions?.setViewMode($event);
  }

  onPageChange(event: any) {
    this.searchOptions?.setCurrentPage(event.page + 1);
    this.searchOptions?.setPageSize(event.rows);
    this.search.emit(this.searchParam);
  }

  onSubmit(searchParams?: any) {
    if (!searchParams && this.eoForm)
      searchParams = EoFormPanelConfig.getFormData(this.eoForm.getForm());

    this.searchParam = searchParams;
    this.searchOptions?.setCurrentPage(1);
    this.search.emit(this.searchParam);
  }

  onViewModeSelect(mode: 'GRID' | 'TABLE') {
    this.searchOptions?.setViewMode(mode);
  }

  onClear() {
    this.searchService?.init();
    this.eoForm?.form.reset();
    let searchParams = {};

    if (this.eoForm)
      searchParams = EoFormPanelConfig.getFormData(this.eoForm.getForm());

    if (this.initData) this.onSubmit(searchParams);
    this.clearEvent.emit();
  }

  onFormChange($event: any): void {
    this.formChange.emit($event);
  }

  isMessageParams(message: any): message is messageParams {
    return (message as messageParams) !== undefined;
  }

  showSuccessMessage(
    message: messageParams | string,
    header: string = ' '
  ) {
    if (this.isMessageParams(message)) {
      let header = message.header;
      if (!header) {
        header = ' ';
      }

      this.confirmationService?.confirm({
        header: header,
        icon: 'pi pi-check-circle',
        message: message.message ?? 'Η διαδικασία ολοκληρώθηκε με επιτυχία.',
        rejectLabel: 'Εντάξει',
        rejectIcon: '-',
        rejectButtonStyleClass: 'p-button-text',
        acceptVisible: false,
        accept: message.accept,

        closeOnEscape: true,
        key: 'searchPageMessage',
      });
    } else {
      this.confirmationService?.confirm({
        header: header,
        icon: 'pi pi-check-circle',
        message: message,
        rejectLabel: 'Εντάξει',
        rejectIcon: '-',
        rejectButtonStyleClass: 'p-button-text',
        acceptVisible: false,
        closeOnEscape: true,
        key: 'searchPageMessage',
      });
    }
  }

  isGridView(): boolean {
    return this.viewOptions$?.getValue().viewMode === 'GRID';
  }

  isTableView(): boolean {
    return this.viewOptions$?.getValue().viewMode === 'TABLE';
  }

  isLoading(): boolean {
    const searchStatus = this.searchServ?.searchCh?.status$.getValue();
    return searchStatus === STATUS.loading;
  }

  isSearchPending(): boolean {
    const searchStatus = this.searchServ?.searchCh?.status$.getValue();
    return searchStatus === STATUS.success;
  }

  isSearchSuccess(): boolean {
    const searchStatus = this.searchServ?.searchCh?.status$.getValue();
    return searchStatus === STATUS.success;
  }

  getResults(): T[] | null {
    const searchResults = this.searchServ?.searchCh?.data$.getValue();
    return searchResults?.Resultset ?? null;
  }

  getTotalCount(): number {
    const searchResults = this.searchService?.searchCh?.data$.getValue();
    return this.totalCount ?? searchResults?.TotalCount ?? 0;
  }

  getPage(): number {
    return this.page ?? this.viewOptions$?.getValue().currentPage ?? 0;
  }

  getPagesize(): number {
    return this.pagesize ?? this.viewOptions$?.getValue().pageSize ?? 10;
  }

  getFormConfig() {
    return this.formConfig;
  }

  isSubmitLoading(): boolean {
    let status = this.searchService?.searchCh?.status$?.getValue();
    return status === STATUS.loading;
  }

  getTableConfig(): TableConfig<T> | undefined {
    return undefined;
  }

  isNoDataLabelVisible(): boolean {
    return this.isSearchSuccess() && this.getTotalCount() === 0;
  }

  isTableVisible(): boolean {
    if (!this.isTableView()) return false;
    if (!this.isLoading() && this.getTotalCount() === 0) return false;
    return true;
  }

  isPaginatorVisible(): boolean {
    if (!this.paginator) return false;
    return this.getTotalCount() > 0;
  }

  getShowClearButton(): boolean {
    return this.showClearButton
  }
}
